import { createFileRoute, Link } from "@tanstack/react-router";
import { ACTIVATIONS } from "@/lib/content";
import { absoluteUrl } from "@/lib/site";

export const Route = createFileRoute("/activations/wheel")({
  head: () => ({
    meta: [
      { title: "Daily Activation Wheel · Buddha KA" },
      { name: "description", content: "A circular map of the day's four activations — rising, midday, evening, and night — one gesture to enter each." },
      { property: "og:title", content: "Daily Activation Wheel · Buddha KA" },
      { property: "og:description", content: "Rising · Midday · Evening · Night — the daily wheel of activations." },
      { property: "og:url", content: absoluteUrl("/activations/wheel") },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/activations/wheel") }],
  }),
  component: WheelPage,
});

const QUADRANTS: Array<{ slug: string; label: string; angle: number; hint: string }> = [
  { slug: "rising-activation", label: "Rising", angle: -90, hint: "Dawn · Ignite the field" },
  { slug: "midday-activation", label: "Midday", angle: 0, hint: "Noon · Command the flame" },
  { slug: "evening-activation", label: "Evening", angle: 90, hint: "Dusk · Compose the descent" },
  { slug: "night-seal", label: "Night", angle: 180, hint: "Night · Seal the field" },
];

function currentQuadrant(): string {
  const h = new Date().getHours();
  if (h < 11) return "rising-activation";
  if (h < 15) return "midday-activation";
  if (h < 20) return "evening-activation";
  return "night-seal";
}

function WheelPage() {
  const active = currentQuadrant();
  const size = 520;
  const cx = size / 2;
  const cy = size / 2;
  const r = 200;

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20">
      <header className="text-center" data-reveal="up">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">The Wheel</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl">Daily Activation Wheel</h1>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
          Four gates. One day. Enter each at its hour, or press the one calling you now.
        </p>
      </header>

      <div className="mt-12 flex justify-center" data-reveal="up">
        <svg
          viewBox={`0 0 ${size} ${size}`}
          className="w-full max-w-[520px]"
          role="img"
          aria-label="Circular activation wheel with four quadrants"
        >
          <defs>
            <radialGradient id="wheel-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.2" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </radialGradient>
          </defs>
          <g className="text-gold">
            <circle cx={cx} cy={cy} r={r + 40} fill="url(#wheel-glow)" />
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="currentColor" strokeOpacity="0.35" />
            <circle cx={cx} cy={cy} r={r * 0.7} fill="none" stroke="currentColor" strokeOpacity="0.2" />
            <circle cx={cx} cy={cy} r={r * 0.35} fill="none" stroke="currentColor" strokeOpacity="0.15" />
            {/* cross axes */}
            <line x1={cx - r} y1={cy} x2={cx + r} y2={cy} stroke="currentColor" strokeOpacity="0.15" />
            <line x1={cx} y1={cy - r} x2={cx} y2={cy + r} stroke="currentColor" strokeOpacity="0.15" />
            <text x={cx} y={cy - 6} textAnchor="middle" className="fill-current text-[10px] uppercase tracking-[0.35em]">Ka</text>
            <text x={cx} y={cy + 10} textAnchor="middle" className="fill-current text-[10px] uppercase tracking-[0.35em]">Frequency</text>
          </g>

          {QUADRANTS.map((q) => {
            const rad = (q.angle * Math.PI) / 180;
            const x = cx + Math.cos(rad) * r;
            const y = cy + Math.sin(rad) * r;
            const isActive = q.slug === active;
            const act = ACTIVATIONS.find((a) => a.slug === q.slug);
            return (
              <g key={q.slug}>
                <Link to="/activations/$slug" params={{ slug: q.slug }} aria-label={`${q.label} — ${act?.title ?? q.slug}`}>
                  <circle
                    cx={x} cy={y} r={44}
                    className={
                      isActive
                        ? "fill-[color:var(--gold)] stroke-[color:var(--gold-luminous)]"
                        : "fill-[color:var(--charcoal)] stroke-[color:var(--gold)]"
                    }
                    strokeWidth={isActive ? 2 : 1}
                  />
                  <text
                    x={x} y={y - 2} textAnchor="middle"
                    className={isActive ? "fill-[color:var(--obsidian)]" : "fill-[color:var(--gold-luminous)]"}
                    style={{ fontFamily: "var(--font-serif)", fontSize: 18 }}
                  >
                    {q.label}
                  </text>
                  <text
                    x={x} y={y + 14} textAnchor="middle"
                    className={isActive ? "fill-[color:var(--obsidian)]" : "fill-[color:var(--muted-foreground)]"}
                    style={{ fontSize: 9, letterSpacing: 2 }}
                  >
                    {isActive ? "NOW" : ""}
                  </text>
                </Link>
              </g>
            );
          })}
        </svg>
      </div>

      <ul className="mx-auto mt-10 grid max-w-3xl gap-3 sm:grid-cols-2" data-reveal="up">
        {QUADRANTS.map((q) => {
          const act = ACTIVATIONS.find((a) => a.slug === q.slug);
          return (
            <li key={q.slug}>
              <Link
                to="/activations/$slug"
                params={{ slug: q.slug }}
                className="card-luxe hover-lift block rounded-2xl border hairline bg-charcoal/40 p-5"
              >
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{q.hint}</p>
                <p className="mt-2 font-serif text-xl text-ivory">{act?.title ?? q.label}</p>
                <p className="mt-1 text-sm text-muted-foreground">{act?.duration}</p>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
